import { useRef, useState } from "react";

interface Props {
  label: string;
  token: string;
  warn?: string;
}

type CopyState = "idle" | "copied" | "failed";

// `navigator.clipboard` is only available in secure contexts (HTTPS or
// localhost). On a LAN HTTP dev URL it is undefined, so we fall back to
// selecting the visible <code> and using execCommand('copy'), and finally
// to selecting it for the user to ⌘C themselves.
async function copyToClipboard(text: string, srcEl: HTMLElement | null): Promise<boolean> {
  // Modern API.
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      /* fall through */
    }
  }
  // execCommand fallback — works on plain HTTP.
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    if (ok) return true;
  } catch {
    /* fall through */
  }
  // Last resort — select the visible token so the user can ⌘C / Ctrl-C themselves.
  if (srcEl) {
    const range = document.createRange();
    range.selectNodeContents(srcEl);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }
  return false;
}

export function TokenDisplay({ label, token, warn }: Props) {
  const [state, setState] = useState<CopyState>("idle");
  const codeRef = useRef<HTMLElement>(null);
  const onCopy = async () => {
    const ok = await copyToClipboard(token, codeRef.current);
    setState(ok ? "copied" : "failed");
    setTimeout(() => setState("idle"), 1800);
  };
  return (
    <div>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          color: "var(--color-text-muted)",
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div className="token-box">
        <code
          ref={codeRef}
          style={{ flex: 1, cursor: "text", userSelect: "all" }}
          onClick={(e) => {
            const range = document.createRange();
            range.selectNodeContents(e.currentTarget);
            const sel = window.getSelection();
            sel?.removeAllRanges();
            sel?.addRange(range);
          }}
        >
          {token}
        </code>
        <button className="copy" onClick={onCopy} aria-label={`Copy ${label}`}>
          {state === "copied" ? "✓ copied" : state === "failed" ? "select & ⌘C" : "copy"}
        </button>
      </div>
      {warn && <div className="token-warn">⚠ {warn}</div>}
    </div>
  );
}
